import React from "react";
import { useDraggable } from "@dnd-kit/core";
import placeholder from "../../../assets/player_placeholder.png";
import { getPlayerPhoto } from "../functions";
import { useIsMobileBreakpoint } from "../../../hooks/useIsMobileBreakpoint";

/**
 * Construye el aria-label descriptivo para un jugador
 */
function getPlayerAriaLabel(player, isCurrentSlot, isAssigned) {
  const fullName = `${player.firstName} ${player.lastName}`;
  if (isCurrentSlot) return `${fullName} (asignado actualmente)`;
  if (isAssigned) return `${fullName} (asignado)`;
  return fullName;
}

/**
 * Contenido visual compartido entre ambas versiones del item
 */
function PlayerItemContent({ player, isCurrentSlot, isAssigned, accentColor, nameColor, lastNameColor, showGrip, showClubs, showNationality, selectionPath }) {
  const isMobileSmall = useIsMobileBreakpoint(480);

  return (
    <>
      {showGrip && (
        <span className="player-list__item-grip" aria-hidden="true" role="presentation">⠿</span>
      )}
      <div className="player-list__item-avatar" role="presentation">
        <img
          src={player.photo ? getPlayerPhoto(player.photo, selectionPath) : placeholder}
          alt={`Foto de ${player.firstName} ${player.lastName}`}
          onError={(e) => { 
            e.target.src = placeholder; 
          }}
        />
      </div>
      <div className="player-list__item-name">
        <span className="player-list__item-fullname">
          <span className="player-list__item-nombre"
            style={isCurrentSlot && nameColor ? { color: nameColor } : undefined}
          >
            {isMobileSmall ? String(player.firstName).toUpperCase().charAt(0) + "." : String(player.firstName).toUpperCase()}
          </span>
          {" "}
          <span className="player-list__item-apellido"
            style={lastNameColor ? { color: lastNameColor } : undefined}
          >
            {String(player.lastName).toUpperCase()}
          </span>
        </span>
        {showClubs && player.club && (
          <span className="player-list__item-club">
            {String(player.club).toUpperCase()}
          </span>
        )}
        {showNationality && player.nationality && (
          <span className={`player-list__item-nationality bandera-${player.nationality}`}
            title={player.nationality}
            aria-label={`Nacionalidad: ${player.nationality}`}
          />
        )}
      </div>
      {isCurrentSlot && (
        <span className="player-list__item-dot"
          style={accentColor ? { background: accentColor } : undefined}
          aria-hidden="true"
          role="presentation"
        />
      )}
      {isAssigned && !isCurrentSlot && (
        <span className="player-list__item-check" aria-hidden="true" role="presentation">✓</span>
      )}
    </>
  );
}

/**
 * Versión simple del item (sin drag & drop) — usada en mobile/tablet
 */
const SimplePlayerItem = React.memo(function SimplePlayerItem({ player, isAssigned, isCurrentSlot, isClickable, accentColor, nameColor, lastNameColor, onClick, showClubs, showNationality, selectionPath }) {
  const ariaLabel = getPlayerAriaLabel(player, isCurrentSlot, isAssigned);

  return (
    <li
      className={[
        "player-list__item",
        isAssigned && "player-list__item--assigned",
        isCurrentSlot && "player-list__item--current",
        isClickable && "player-list__item--clickable",
      ].filter(Boolean).join(" ")}
      onClick={onClick}
      role="listitem"
      aria-label={ariaLabel}
      aria-disabled={!isClickable}
      tabIndex={isClickable ? 0 : -1}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <PlayerItemContent
        player={player}
        isCurrentSlot={isCurrentSlot}
        isAssigned={isAssigned}
        accentColor={accentColor}
        nameColor={nameColor}
        lastNameColor={lastNameColor}
        showGrip={false}
        showClubs={showClubs}
        showNationality={showNationality}
        selectionPath={selectionPath}
      />
    </li>
  );
});

/**
 * Versión con drag & drop del item — usada en desktop
 */
const DraggablePlayerItem = React.memo(function DraggablePlayerItem({ player, isAssigned, isCurrentSlot, isClickable, accentColor, nameColor, lastNameColor, onClick, showClubs, showNationality, selectionPath }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `player-${player.id}`,
    data: { playerId: player.id },
  });

  const ariaLabel = getPlayerAriaLabel(player, isCurrentSlot, isAssigned);

  return (
    <li
      ref={setNodeRef}
      className={[
        "player-list__item",
        isAssigned && "player-list__item--assigned",
        isCurrentSlot && "player-list__item--current",
        isClickable && "player-list__item--clickable",
        isDragging && "player-list__item--dragging",
      ].filter(Boolean).join(" ")}
      onClick={onClick}
      role="listitem"
      aria-label={ariaLabel}
      aria-disabled={!isClickable}
      tabIndex={isClickable ? 0 : -1}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      {...listeners}
      {...attributes}
    >
      <PlayerItemContent
        player={player}
        isCurrentSlot={isCurrentSlot}
        isAssigned={isAssigned}
        accentColor={accentColor}
        nameColor={nameColor}
        lastNameColor={lastNameColor}
        showGrip={true}
        showClubs={showClubs}
        showNationality={showNationality}
        selectionPath={selectionPath}
      />
    </li>
  );
});

/**
 * Componente wrapper que decide qué versión renderizar
 */
export default React.memo(function PlayerItem(props) {
  if (props.disableDrag) {
    return <SimplePlayerItem {...props} />;
  }
  return <DraggablePlayerItem {...props} />;
});